// Native one-way mirror of the syncable state to Firestore (users/{uid}),
// behind a guarded require like the rest of the native Firebase modules. Web
// is firestoreStore.web.ts (Firebase JS SDK). No-ops until
// @react-native-firebase/firestore is installed + the app rebuilt.

import { Platform } from 'react-native';

import { SyncableState } from './merge';

/* eslint-disable @typescript-eslint/no-explicit-any */
let mod: any | null = null;
let attempted = false;

const getFirestore = (): any | null => {
  if (Platform.OS === 'web') return null;
  if (attempted) return mod;
  attempted = true;
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    mod = require('@react-native-firebase/firestore').default;
  } catch {
    mod = null;
  }
  return mod;
};

// Mirror the user's syncable state to users/{uid}. merge:true so fields written
// separately (e.g. fcmTokens) are preserved. Best-effort; no-ops without a uid
// / Firestore.
export const writeUserState = async (uid: string, state: SyncableState): Promise<void> => {
  const fs = getFirestore();
  if (!fs || !uid) return;
  try {
    // `hasAlerts` lets the scheduled notifier query only users with alerts
    // (`where('hasAlerts','==',true)`) instead of scanning the whole users
    // collection every hour. Derived on every write so it stays in sync.
    const payload = { ...state, hasAlerts: (state.alerts?.length ?? 0) > 0 };
    await fs().collection('users').doc(uid).set(payload, { merge: true });
  } catch {
    /* best-effort backup */
  }
};

// Read the user's backed-up state from users/{uid}. Returns null ONLY when
// there's genuinely no remote copy — no Firestore backend, no uid, or no
// document yet (new account). A real read FAILURE (network/permission) is
// allowed to throw so the caller aborts the sign-in sync instead of treating it
// as "no remote" and overwriting the cloud backup with (possibly empty) local
// state on a transient error.
export const readUserState = async (uid: string): Promise<SyncableState | null> => {
  const fs = getFirestore();
  if (!fs || !uid) return null;
  const snap = await fs().collection('users').doc(uid).get();
  // RNFirebase exposes `exists` as a METHOD (web JS SDK does too) — calling
  // it is required; `snap.exists` alone is the always-truthy function ref.
  if (!snap.exists()) return null;
  const d = snap.data();
  return d ? (d as SyncableState) : null;
};

// Delete the user's backed-up document (account deletion). Throws on failure so
// the caller can abort before removing the auth account, leaving nothing
// orphaned. No-ops without Firestore / a uid.
export const deleteUserState = async (uid: string): Promise<void> => {
  const fs = getFirestore();
  if (!fs || !uid) return;
  await fs().collection('users').doc(uid).delete();
};

export default { writeUserState, readUserState, deleteUserState };
